import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useRef } from 'react';
import { DynamicForm } from 'src/components';
import { useUserGroupOptions } from 'src/graphql';
import {
  AttributeDefinition,
  getFilters,
  useCustomAttributeDefinition,
} from 'src/graphql/entities/custom-attributes/custom-attributes';
import { parseOptionsInput } from 'src/graphql/entities/custom-attributes/parse-options-input';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { parseJsonSafely } from 'src/utils/objects';
import { slugify } from 'src/utils/strings';
import {
  CustomAttributeFormValues,
  useCustomAttributeForm,
} from './use-custom-attribute-form';

export const AddCustomAttributeModal = () => {
  const { closeModal } = useModalMethods(Modals.AddCustomAttributeModal);
  const { isOpened, modalProps } = useModalState(Modals.AddCustomAttributeModal);

  const { customAttribute, entity, customAttributeDefinition } = modalProps ?? {};

  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const filters = useMemo(
    () => getFilters({ partnerId: selectedPartnerId }),
    [selectedPartnerId],
  );

  const { saveCustomAttributeDefinition } = useCustomAttributeDefinition({
    filters,
  });

  const { showSuccessAlert, showErrorAlert } = useNotifications();

  const { userGroupOptions } = useUserGroupOptions();

  const formValues = useMemo(() => {
    if (!customAttribute) {
      return {
        restrictedToUserGroups: [] as {
          id: string;
          label: string;
        }[],
      } as CustomAttributeFormValues;
    }

    return {
      ...customAttribute,
      options: customAttribute.options?.map((option) => option.value).join('\n'),
      restrictAccess: !customAttribute.visibility.visibleToAllUsers,
      group: customAttribute.group
        ? { id: customAttribute.group, label: customAttribute.group }
        : undefined,
      restrictedToUserGroups:
        customAttribute.visibility.restrictedToUserGroups?.filter(Boolean).map(
          (id) =>
            userGroupOptions.find((userGroup) => userGroup.id === id) ?? {
              id,
              label: id,
            },
        ) ?? [],
    } as CustomAttributeFormValues;
  }, [customAttribute, userGroupOptions]);

  const { attributeFormFields } = useCustomAttributeForm(
    customAttributeDefinition,
    formValues,
  );

  const submitButtonRef = useRef<HTMLButtonElement | null>(null);
  const triggerSubmit = useCallback(() => {
    submitButtonRef?.current?.click();
  }, []);

  const handleAddCustomAttribute = useCallback(
    async (values: CustomAttributeFormValues) => {
      let definition = {
        attributes: {},
      } as AttributeDefinition;

      if (customAttributeDefinition?.definition) {
        definition =
          parseJsonSafely<AttributeDefinition>(customAttributeDefinition.definition) ||
          definition;
      }

      const isAttributeDuplicated =
        values.displayName &&
        definition?.attributes?.[values.displayName] &&
        customAttribute?.displayName !== values.displayName &&
        !definition.attributes[values.displayName].deleted;

      if (isAttributeDuplicated) {
        showErrorAlert(
          `Unable to ${customAttribute ? 'update' : 'create'} property. Property ${values.displayName} is already being used.`,
        );

        return;
      }

      if (!values.displayName) {
        return;
      }

      const restrictAccess = values.restrictAccess ?? false;

      const existingKey = customAttribute?.displayName
        ? definition.attributes[customAttribute.displayName]?.key
        : undefined;

      definition.attributes[values.displayName] = {
        ...(existingKey ? { key: existingKey } : {}),
        displayName: values.displayName,
        visibility: {
          visibleToAllUsers: !restrictAccess,
          restrictedToUserGroups: restrictAccess
            ? (values.restrictedToUserGroups?.map((group) => group.id) ?? [])
            : [],
        },
        defaultValue: values.defaultValue,
        type: values.type?.id!,
        deleted: false,
      };

      const parsedOptions = parseOptionsInput(values.options);

      if (parsedOptions.length > 0) {
        definition.attributes[values.displayName].options = parsedOptions.map((value) => ({
          key: slugify(value),
          value,
        }));
      }

      if (values.relatedEntity) {
        definition.attributes[values.displayName].relatedEntity = values.relatedEntity.id;
      }

      if (values.group) {
        definition.attributes[values.displayName].group = values.group.id;
      }

      if (
        customAttribute?.displayName &&
        values.displayName !== customAttribute?.displayName
      ) {
        delete definition.attributes[customAttribute.displayName];
      }

      closeModal();

      const response = await saveCustomAttributeDefinition({
        customAttributeDefinition,
        definition: JSON.stringify(definition),
        entity,
        partnerId: selectedPartnerId!,
      });

      if (!response) {
        return;
      }

      showSuccessAlert(customAttribute ? 'Property was updated' : 'Property was added');
    },
    [
      customAttributeDefinition,
      customAttribute,
      closeModal,
      saveCustomAttributeDefinition,
      entity,
      selectedPartnerId,
      showSuccessAlert,
      showErrorAlert,
    ],
  );

  return (
    <Dialog
      open={isOpened}
      onClose={closeModal}
      fullWidth
      PaperProps={{
        sx: {
          border: '1px solid',
          borderColor: (theme) => theme.palette.custom.OV.sys.outlineVariant,
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid',
          borderColor: (theme) => theme.palette.custom.OV.sys.outlineVariant,
        }}
      >
        <Stack direction="row" spacing={2}>
          <Typography variant="overline">
            {customAttribute ? 'Update Property' : 'Add Property'}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack
          gap={2}
          sx={{
            paddingTop: 2,
          }}
        >
          <DynamicForm
            style={{ gap: '1rem' }}
            onSubmit={handleAddCustomAttribute}
            fields={attributeFormFields}
            value={formValues ?? undefined}
          >
            <Button
              type="submit"
              ref={submitButtonRef}
              sx={{
                display: 'none',
              }}
            >
              Save
            </Button>
          </DynamicForm>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={triggerSubmit}
          sx={{
            color: (theme) => theme.palette.custom.OV.sys.primary,
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
